import JSON5 from 'json5';
import { GuidanceTextResponse } from '../utils';
import { defaultCostValue } from './RelevancyCheckbox';

export const budgetQuestionIds = [80003, 80005, 80007];
export const DIRECT_COST_QUESTION_ID = 80003;
export const IN_KIND_DONATIONS_QUESTION_ID = 80005;
export const OVERHEAD_COST_QUESTION_ID = 80007;
export const BUDGET_FILE_QUESTION_ID = 80009;

export const INTERIM_BUDGET_QUESTION_ID = 120005;
export const FINAL_BUDGET_QUESTION_ID = 90005;

export const defaultBudgetValue = '';

export const getTotalBudget = (
  a: string | undefined,
  b: string | undefined,
  c: string | undefined,
): number => {
  try {
    return (
      (!a || a === defaultCostValue ? 0 : Number(a.replace(/,/g, ''))) +
      (!b || b === defaultCostValue ? 0 : Number(b.replace(/,/g, ''))) +
      (!c || c === defaultCostValue ? 0 : Number(c.replace(/,/g, '')))
    );
  } catch (error) {
    return 0;
  }
};

export const getProgramNameUrl = (programName: string | undefined): string => {
  return programName?.replace(/[^a-zA-Z0-9-]/g, '-') || 'TI';
};

export interface BudgetValue {
  answerId: number;
  type: 'currency_multi' | string;
  text?:
    | {
        id?: number;
        url?: string;
        currency?: string;
        fileName?: string;
        description?: string;
      }
    | string;
}

export const getBudgetDollarAmount = (value?: BudgetValue | string): string => {
  if (!value) return defaultBudgetValue;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value).currency || defaultBudgetValue;
    } catch (e) {
      return defaultBudgetValue;
    }
  }
  return typeof value.text === 'string'
    ? defaultBudgetValue
    : value.text?.currency || defaultBudgetValue;
};

export interface BudgetSubmitReference {
  error: () => string | undefined;
  handleSubmit: () => string;
}

export interface BudgeComponentProps {
  reportId: number | string;
  hideBottomBar?: boolean;
  isReportPassed?: boolean;
  guidanceText: GuidanceTextResponse;
  onNext: (value: string) => unknown;
  onPrevious: () => unknown;
}

export interface BudgetDollarComponent {
  initialValue: string;
}

export interface KindDonation {
  text: string;
  hours: string;
  perHour: string;
  total: string;
  disabledTotal?: boolean;
  disabledHourly?: boolean;
  errorInHoursField?: boolean;
  errorInPerHoursField?: boolean;
}

export const kindDonationsList = (): KindDonation[] => [
  {
    text: 'Volunteerism',
    hours: '',
    perHour: '',
    total: '',
  },
  {
    text: 'Pro bono',
    hours: '',
    perHour: '',
    total: '',
  },
  {
    text: 'Donated goods',
    hours: '',
    perHour: '',
    total: '',
  },
  {
    text: 'Donated services',
    hours: '',
    perHour: '',
    total: '',
  },
];

export interface Cost {
  text: string;
  value: string;
}

export const defaultCostList: Cost[] = [
  { text: 'Salaries', value: '' },
  { text: 'Equipment', value: '' },
  { text: 'Materials, Supplies', value: '' },
  { text: 'Communication', value: '' },
  { text: 'Travel', value: '' },
  { text: 'Professional Services', value: '' },
  { text: 'Miscellaneous', value: '' },
];

export const defaultOverheadCostList: Cost[] = [
  { text: 'Rent', value: '' },
  { text: 'Advertising', value: '' },
  { text: 'Utilities', value: '' },
  { text: 'Professional Services', value: '' },
  { text: 'Insurance', value: '' },
  { text: 'Government fees and licenses', value: '' },
  { text: 'Office supplies', value: '' },
  { text: 'Property taxes', value: '' },
  { text: 'Travel', value: '' },
  { text: 'Other', value: '' },
];

export const saveBudgetAmountValue = (value: string): string =>
  JSON.stringify({ currency: value });

export interface FinalBudget {
  directCost: string;
  overheadCost: string;
  inKindDonations: string;
}

export const getGetBudgetQuestionProperty = (
  questionId: number,
): 'directCost' | 'overheadCost' | 'inKindDonations' | undefined => {
  switch (questionId) {
    case DIRECT_COST_QUESTION_ID:
      return 'directCost';
    case OVERHEAD_COST_QUESTION_ID:
      return 'overheadCost';
    case IN_KIND_DONATIONS_QUESTION_ID:
      return 'inKindDonations';
  }
};

export const saveFinalBudget = (
  directCost: string | undefined,
  overheadCost: string | undefined,
  inKindDonations: string | undefined,
): FinalBudget => {
  return {
    directCost: directCost || '',
    overheadCost: overheadCost || '',
    inKindDonations: inKindDonations || '',
  };
};

export const getFinalizedBudget = (
  value: string | { text: FinalBudget } | undefined,
): FinalBudget | undefined => {
  if (!value) return undefined;
  try {
    const result = typeof value === 'object' ? value.text : JSON5.parse(value);
    return result;
  } catch (e) {
    return undefined;
  }
};

export const saveFinalBudgeAmount = (
  questionId: number,
  answer: string,
  previous: string | { text: FinalBudget } | undefined,
): FinalBudget => {
  const current = getFinalizedBudget(previous);
  const property = getGetBudgetQuestionProperty(questionId);
  const result: FinalBudget = saveFinalBudget(
    current?.directCost,
    current?.overheadCost,
    current?.inKindDonations,
  );
  if (!property) return result;
  result[property] = answer;
  return result;
};
